import React, { useCallback, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { Button } from 'ksnm-common-ui/lib/Button';
import { Icon } from 'ksnm-common-ui/lib/Icon';
import { Modal } from 'ksnm-common-ui/lib/Modal';
import { Table } from 'ksnm-common-ui/lib/Table2';
import { getLocalRowsByCheckBoxStatus } from 'ksnm-common-ui/lib/Table2/utils/getLocalRowsByCheckBoxStatus';
import type { LocalDataSource } from 'ksnm-common-ui/lib/Table2/contexts/Table/TableContext.interface';
import type { ResponseDataRow } from 'ksnm-common-ui/lib/Table2/utils/TableRequest';

import { cmApi } from '@src/api';
import { useUpdateTable } from '@src/utils/hooks/useUpdateTable';
import { constantStore } from '@stores/constantStore';
import { preloaderStore } from '@stores/preloaderStore';
import { publicationRegisterStore } from '@stores/publicationRegisterStore';
import { userStore } from '@stores/userStore';

import styles from './RejectModal.module.scss';

interface IRejectModal {
  open: boolean;
  toggle: () => void;
  update: () => void;
}

const {
  TABLE_NAMES,
  REPORT_PACKAGE_STATUS: { NEEDS_IMPROVEMENT },
} = constantStore;

export const RejectModal = observer(({ open, toggle, update }: IRejectModal) => {
  const [selected, setSelected] = useState<ResponseDataRow[]>([]);

  const { rejectModal, updateReportPackageStatus } = publicationRegisterStore;
  const { user } = userStore;

  const { reports, packageCode } = rejectModal || {};

  const columns = useMemo(
    () => [
      {
        title: '',
        key: 'name',
        width: 200,
      },
      {
        key: 'reportName',
        title: 'Наименование',
      },
      {
        title: 'Тип отчета',
        key: 'reportTypeCode',
        width: 200,
      },
      {
        title: 'Статус',
        key: 'reportPublishStatus',
        width: 200,
      },
    ],
    [],
  );

  const localData = useMemo<LocalDataSource>(
    () => ({
      data: reports ?? [],
      fieldFormat: { name: 'string', reportName: 'string', reportTypeCode: 'string', reportPublishStatus: 'string' },
    }),
    [reports],
  );

  const [key, updateTable, externalControl] = useUpdateTable({
    tableName: TABLE_NAMES.PUBLICATION_REJECT_REPORT_MODAL,
  });

  const onSubmit = useCallback(() => {
    (async () => {
      preloaderStore.preloader(true);

      try {
        const promises = selected.map((item) =>
          cmApi.reportPublish.updateReportPublish({ ...item, status: NEEDS_IMPROVEMENT, username: user?.fio }),
        );
        await Promise.all(promises);

        if (packageCode) {
          await updateReportPackageStatus(packageCode, NEEDS_IMPROVEMENT);
        }

        update();
      } catch (error) {
        console.error(error);
      }

      preloaderStore.preloader(false);

      updateTable();
      toggle();
    })();
  }, [packageCode, selected, toggle, update, updateReportPackageStatus, updateTable, user?.fio]);

  const onCheckBoxChange = useCallback(
    async (tr, sr) => {
      const rows = await getLocalRowsByCheckBoxStatus({
        tableRequest: tr,
        localData,
        selectRows: sr,
        keyField: 'id',
      });
      setSelected(rows);
    },
    [localData],
  );

  return (
    <Modal
      open={open}
      className={styles.modal}
      centered
      footer={null}
      onCancel={toggle}
      width={1600}
      closeIcon={<Icon name="close" noFill height={24} />}
    >
      <div className={styles.container}>
        <h1 className={styles.title}>Проверка отклоненных отчетов</h1>

        <Table
          externalControl={externalControl}
          resource="reject_modal"
          key={key}
          keyField="id"
          columns={columns}
          localDataSource={localData}
          showAggregateRow={false}
          cellHeight={40}
          doNotShowGroupPanel
          disableChangeGroup
          tableName={TABLE_NAMES.PUBLICATION_REJECT_REPORT_MODAL}
          onCheckBoxChange={onCheckBoxChange}
        />

        <div className={styles.button}>
          <Button disabled={!selected.length} key="submit" onClick={onSubmit}>
            Отклонить пакет
          </Button>

          <Button onClick={toggle} key="cancel">
            Отменить
          </Button>
        </div>
      </div>
    </Modal>
  );
});
